import ky, { HTTPError } from 'ky';
import { getCSRF, HOST } from './request';

/**
 * Configured `ky` instance carrying the project-wide HTTP policy: CSRF header
 * injection, same-origin credentials, retries off (React Query owns retries),
 * a 30s default timeout, and a 401 → reload guard.
 *
 * Callers use ky's native API directly through this instance, e.g.
 * `api.post('queries/packages/master_data', { json, signal }).json<T>()`.
 *
 * NOTE: because `prefixUrl` is set, request paths MUST NOT start with a leading
 * slash (ky throws otherwise). This diverges from `request.ts`, which accepts
 * leading slashes.
 */
/** Request timeout shared by `api` and `externalApi`. */
const TIMEOUT_MS = 30_000;

export const api = ky.create({
  prefixUrl: HOST || '/',
  credentials: 'same-origin',
  retry: 0,
  timeout: TIMEOUT_MS,
  hooks: {
    beforeRequest: [
      /**
       * Injects the shared CSRF token into every outbound request.
       *
       * @param request - The outgoing request whose headers are mutated in place.
       */
      (request) => request.headers.set('X-CSRF-TOKEN', getCSRF()),
    ],
    beforeError: [
      /**
       * Reloads the page on a 401 so the user re-authenticates.
       *
       * Guards on `HTTPError` because ky also routes `TimeoutError` and network
       * `TypeError`s through `beforeError`, and those carry no `response`.
       *
       * @param error - The ky error (any of HTTPError / TimeoutError / network TypeError).
       * @return The error unchanged, for ky to continue rejecting with.
       */
      (error) => {
        if (error instanceof HTTPError && error.response.status === 401) {
          location.reload();
        }
        return error;
      },
    ],
  },
});

/**
 * `ky` instance for URLs outside the Rails app: Azure SAS blobs, signed URLs
 * and other sites' tiles. Callers pass the full URL, e.g.
 * `externalApi.get(sasUrl, { signal }).json<T>()`.
 *
 * A separate `ky.create`, not `api.extend`, so none of `api`'s policy carries
 * over to another site: no prefix, no cookies, no CSRF token, and no reload on
 * a 401, which from another site means an expired link rather than a lost
 * session. Retries stay off (React Query owns them) and the timeout matches
 * `api`'s 30s; a large download can pass `timeout: false` on its own call.
 */
export const externalApi = ky.create({
  credentials: 'omit',
  retry: 0,
  timeout: TIMEOUT_MS,
});

/**
 * The message a failed read through ky carries: a 5xx is 'Internal Server Error'; a 4xx is the
 * body's `error` or first `errors[].message`, else the status text; a request that never got an
 * answer (offline, timed out) is 'Failed to fetch resource'.
 *
 * @param {unknown} error - What ky threw.
 * @return {Promise<string>} The message.
 */
export async function failureMessage(error: unknown): Promise<string> {
  if (!(error instanceof HTTPError)) return 'Failed to fetch resource';

  const { response } = error;
  if (response.status >= 500) return 'Internal Server Error';
  try {
    const body = await response.json<{
      error?: unknown;
      errors?: { message?: unknown }[];
    } | null>();
    const message = [body?.error, body?.errors?.[0]?.message].find(
      (value): value is string => typeof value === 'string'
    );
    return message || response.statusText || 'Unknown';
  } catch {
    // A body that isn't JSON has no message of its own; the status text stands in.
    return response.statusText || 'Unknown';
  }
}
